import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Heart } from "lucide-react";
import { AUDIENCES, getSubcatsForAudience } from "@/constants/categories";
import { SUBCAT_ICONS, DEFAULT_SUBCAT_ICON } from "@/constants/categoryIcons";
import { useLang } from "@/context/LanguageContext";

const THEME = {
  paidia: {
    bg: "/bg/paidia_v3.jpg",
    fill: "rgb(188,219,253)",
    title: "text-blue-700",
    link: "text-sky-700 hover:text-sky-800",
    titleEl: "Δραστηριότητες για παιδιά",
    titleEn: "Activities for Kids",
    subEl: "Ανακάλυψε μαθήματα, αθλήματα, δημιουργικές δραστηριότητες και εκδηλώσεις για κάθε ηλικία.",
    subEn: "Discover lessons, sports, creative activities and events for every age.",
  },
  enilikes: {
    bg: "/bg/enilikes_v3.jpg",
    fill: "rgb(158,197,199)",
    title: "text-emerald-700",
    link: "text-emerald-700 hover:text-emerald-800",
    titleEl: "Δραστηριότητες για ενήλικες",
    titleEn: "Activities for Adults",
    subEl: "Εκπαίδευση, άθληση, δημιουργία και εμπειρίες για κάθε ενδιαφέρον.",
    subEn: "Education, fitness, creativity and experiences for every interest.",
  },
};

const SubcatCard = ({ sub, audience, index }) => {
  const navigate = useNavigate();
  const { tt } = useLang();
  const { Icon, color } = SUBCAT_ICONS[sub.slug] || DEFAULT_SUBCAT_ICON;
  return (
    <button
      data-testid="subcategory-card"
      onClick={() => navigate(sub.slug === "athlitikoi" ? `/sports/${audience}` : `/s/${audience}/${sub.slug}`)}
      className="group block w-full text-left rounded-2xl bg-white shadow-md ring-1 ring-black/5 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200"
    >
      <div className="relative">
        <div className="aspect-square overflow-hidden">
          <img src={sub.image} alt={tt(sub)} loading="eager" decoding="sync" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="absolute left-2 bottom-2 z-10 h-9 w-9 rounded-full bg-white shadow-lg ring-1 ring-black/5 grid place-items-center">
          <Icon className={color} size={17} strokeWidth={2.2} />
        </div>
      </div>
      <div className="px-2.5 py-1.5 min-h-[2.6rem] flex items-center">
        <p className="text-slate-900 font-display font-bold text-[11px] sm:text-[13px] leading-tight line-clamp-2">{tt(sub)}</p>
      </div>
    </button>
  );
};

const LangPill = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full bg-white/70 p-0.5 text-xs font-bold shadow-sm" data-testid="page-lang-toggle">
      <button onClick={() => setLang("el")} className={`px-2.5 py-1 rounded-full transition-colors ${lang === "el" ? "bg-primary text-white" : "text-slate-700"}`}>ΕΛ</button>
      <button onClick={() => setLang("en")} className={`px-2.5 py-1 rounded-full transition-colors ${lang === "en" ? "bg-primary text-white" : "text-slate-700"}`}>EN</button>
    </div>
  );
};

const AudiencePage = () => {
  const { audience } = useParams();
  const navigate = useNavigate();
  const { t: tr, tt, lang } = useLang();
  const a = AUDIENCES[audience];
  const t = THEME[audience];
  if (!a) {
    navigate("/");
    return null;
  }
  const subs = getSubcatsForAudience(audience);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundImage: `url(${t.bg})`, backgroundSize: "100% auto", backgroundPosition: "top center", backgroundRepeat: "no-repeat", backgroundColor: t.fill }}
    >
      {/* Top bar (logo + title are part of the background artwork) */}
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between text-slate-900">
          <button onClick={() => navigate("/")} data-testid="audience-back-button" className="h-10 w-10 grid place-items-center rounded-full bg-white/60 hover:bg-white/90 transition-colors shrink-0">
            <ArrowLeft size={22} />
          </button>
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 h-12 w-48" data-testid="audience-logo" aria-label="SkillCity" />
          <LangPill />
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-4 pb-6" style={{ paddingTop: "max(10.5vw, 5rem)" }}>
        <h1 className="sr-only" data-testid="audience-title">{lang === "en" ? t.titleEn : t.titleEl}</h1>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5 sm:gap-6 lg:gap-8" data-testid="subcategory-grid">
          {subs.map((sub, i) => (
            <SubcatCard key={sub.slug} sub={sub} audience={audience} index={i} />
          ))}
        </div>

        <div className="flex justify-center mt-10 mb-6">
          <Link to="/favorites" data-testid="favorites-link" className={`inline-flex items-center gap-2 ${t.link} font-display text-xl font-bold underline underline-offset-4`}>
            <Heart size={18} /> {tr("nav.favorites")}
          </Link>
        </div>
      </main>
    </div>
  );
};

export default AudiencePage;
